import { useState } from "react";
import { PaymentElement, useStripe, useElements } from "@stripe/react-stripe-js";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { CheckCircle2 } from "lucide-react";
import { useLanguage } from "@/lib/language-context";

type Props = {
  onSuccess: () => void;
  onError: (message?: string) => void;
};

export function CardPaymentForm({ onSuccess, onError }: Props) {
  const { t } = useLanguage();
  const stripe = useStripe();
  const elements = useElements();
  const [isConfirming, setIsConfirming] = useState(false);
  const [, setErrorMessage] = useState<string | null>(null);
  const [showSuccessPopup, setShowSuccessPopup] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!stripe || !elements) return;
    setErrorMessage(null);
    setIsConfirming(true);
    try {
      const { error: submitError } = await elements.submit();
      if (submitError) {
        const msg = submitError.message ?? "Payment failed";
        setErrorMessage(msg);
        onError(msg);
        return;
      }
      const returnUrl = `${window.location.origin}${window.location.pathname || "/"}`;
      const { error } = await stripe.confirmPayment({
        elements,
        confirmParams: {
          return_url: returnUrl,
          payment_method_data: {},
        },
      });
      if (error) {
        const msg = error.message ?? "Payment failed";
        setErrorMessage(msg);
        onError(msg);
        return;
      }
      setShowSuccessPopup(true);
    } catch (err) {
      const msg = err instanceof Error ? err.message : "Payment failed";
      setErrorMessage(msg);
      onError(msg);
    } finally {
      setIsConfirming(false);
    }
  };

  const handleSuccessPopupClose = () => {
    setShowSuccessPopup(false);
    onSuccess();
  };

  return (
    <>
      <form onSubmit={handleSubmit} className="space-y-4">
      <PaymentElement
        options={{
          layout: "tabs",
        }}
      />
      <Button
        type="submit"
        disabled={!stripe || !elements || isConfirming}
        size="lg"
        className="w-full h-12 text-lg font-bold rounded-xl"
      >
        {isConfirming ? "Processing…" : "Pay now"}
      </Button>
    </form>

      <Dialog open={showSuccessPopup} onOpenChange={(open) => !open && handleSuccessPopupClose()}>
        <DialogContent showCloseButton={false} className="max-w-sm text-center">
          <DialogHeader>
            <div className="flex justify-center mb-2">
              <CheckCircle2 className="h-14 w-14 text-green-600" aria-hidden />
            </div>
            <DialogTitle className="text-xl">{t.paymentSuccessPopupTitle}</DialogTitle>
          </DialogHeader>
          <p className="text-muted-foreground text-sm">
            {t.paymentSuccessPopupDesc}
          </p>
          <DialogFooter className="flex justify-center sm:justify-center pt-2">
            <Button onClick={handleSuccessPopupClose}>{t.ok}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
